"use client";
import React, {
    Suspense,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    IMG_TRY_ON_NOT_LOGIN_INFO_CACHE,
    USED_IMAGE_TRY_ON,
    USR_PD,
} from "@/config/constConf";
import { USER_PHOTO } from "@/config/interface";
import {
    isServer,
    getSession,
    setSession,
    getLocalItem,
} from "@/lib/commonService";

import { getImgWidthHeight, removeBlank } from "@/src/utils/tryon/ImgUtil";

import useDrag from "./useDrag";
import { fixBody, cancelFixBody } from "@/src/utils/tryon/fixBody";
import { getS3Folder, getFileHash } from "@/src/utils/tryon/common";
import Cropper from "./Cropper";

import { modelImgEyeList } from "./modelImgEyeList";
import SelectLine from "./SelectLine";
import TryOnProductDec from "./TryOnProductDec";
import { gaCommonEvent } from "@/src/utils/gtagEvents";
import variable from "@/src/styles/variables.module.scss";
import {
    FmButton,
    FmDrawer,
    FmIcon,
    FmImage,
    FmLoading,
    FmSelect,
    IContentItem,
} from "@/ui-component";
import stl from "./imageTryon.module.scss";
import classnames from "classnames";
import { defaultPd, processInputPd, wearGlasses } from "./utils";
import { useTranslation } from "@/src/i18n/client";
import {
    isLogin,
    photoListJava,
    postPhotoEyePositionJava,
    unPhoto,
} from "@/src/models/loginModel";
import { postPhotoJava } from "@/src/models/uploadImg";
import { useTryonStore } from "@/store/modules/tryon";
import { use2DImgTryonStore } from "@/store/modules/2dImgTryon";
import TryOnSkeleton from "../CompTryOn/TryOnSkeleton";

interface Props {}

// 图片的最大上传数量
const MAX_UPLOAD_CNT = 50;
// 图片在iphone上的宽高
const imgWidthOnPhone6 = 375;
const ImageTryOn: React.FC<Props> = memo(function ImageTryOn(props) {
    const { t } = useTranslation("common");
    // 用户手机屏幕是不是小屏幕(iphone6、7、8)
    const smallScreen = useMemo(() => {
        return (
            !isServer &&
            ((window.outerWidth === 375 && window.outerHeight === 667) ||
                (window.outerWidth === 414 && window.outerHeight === 736))
        );
    }, []);

    const [hiddenGlass, setHiddenGlass] = useState(false);

    // 本来在设计稿的iPhone6上，高度是430，但是实际上顶部，底部都有一部分区域不能被使用。所以这里设置为375
    const imgHeightOnPhone6 = useMemo(
        () => (smallScreen ? 375 : 430),
        [smallScreen],
    );
    const [imgBoxWidth, imgBoxHeight] = useMemo(
        () =>
            isServer
                ? [imgWidthOnPhone6, imgHeightOnPhone6]
                : [
                      window.innerWidth,
                      (imgHeightOnPhone6 * window.innerWidth) /
                          imgWidthOnPhone6,
                  ],
        [imgHeightOnPhone6],
    );
    // 戴在图片上的眼镜的默认样式
    const defaultStyle = useMemo(() => {
        return {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: "auto",
            width: "276px",
            ...(smallScreen ? { transform: "translateY(30px)" } : {}),
        };
    }, [smallScreen]);

    const [productImg, tryOnProduct, productColorImg] = useTryonStore(
        (state) => [
            state.productImg,
            state.tryOnProduct,
            state.productColorImg,
        ],
    );

    const {
        selectPd,
        imgList,
        usrPd,
        loading,
        usrConfirmImg,
        tooHigh,
        notEyeImgTooHigh,
        lPosition,
        rPosition,
        currImg,
        glassesSize,
        lt,
        mouseLocation,
        style,
        glassesImgSrc,
        glassesColorImgSrc,
        pdModalVisible,
        uploadError,
        notAllowUploadVisible,
        firstNotice,
        imgLoading,
        userUploadFile,
        delBtnLoading,
        noticeShow,
    } = use2DImgTryonStore((state) => state);

    const {
        SET_SELECT_PD,
        SET_USR_PD_H5,
        SET_IMG_LIST,
        SET_USR_CONFIRM_IMG_INFO,
        SET_NOT_ALLOW_UPLOAD,
        SET_FIRST_NOTICE,
        AFTER_SAVE_EYE_POSITION,
        SHOW_IMG,
        SET_L_POSITION,
        SET_R_POSITION,
        SET_GLASSES_INFO,
        SET_GLASSES_COLOR_INFO,
        SET_STYLE,
        SET_PD_MODAL,
        SET_UPLOAD_ERROR,
        SET_IMG_LOADING,
        SET_USR_UPLOAD_FILE,
        SET_DEL_LOADING,
        SET_NOTICE_SHOW,
        SET_UPLOAD_LOADING,
    } = use2DImgTryonStore((state) => state);

    const pdOptions = useMemo(() => {
        const options: IContentItem[] = [
            {
                value: -1,
                label: t("common:ImgTryOn:SelectPd"),
                className: classnames(stl["select-options"], "select-options"),
                disabled: true,
            },
        ];
        for (let i = 45; i <= 80; i += 0.5) {
            options.push({
                value: i,
                label: `${i.toFixed(1)} mm`,
                className: classnames(stl["select-options"], "select-options"),
            });
        }
        return options;
    }, [t]);

    // 移动端有一个select pd的选择器
    const onSelectValChange = useCallback(
        (ev: any) => {
            const pd = processInputPd(ev.target.value);
            SET_SELECT_PD(pd);
        },
        [SET_SELECT_PD],
    );

    // 移动端更改pd之后，需要点击确定之后才会生效
    const onMobilePdChange = useCallback(() => {
        const payload = processInputPd(selectPd);
        gaCommonEvent("PD", {
            event_category: "try on",
            event_label: selectPd,
        });
        SET_USR_PD_H5(payload);
    }, [SET_USR_PD_H5, selectPd]);

    // 从java接口获取图片列表
    const getPhotoList = useCallback(() => {
        photoListJava().then((list) => {
            // @ts-ignore
            const imgList: USER_PHOTO[] = [].concat(list, modelImgEyeList);
            SET_IMG_LIST(imgList);
        });
    }, [SET_IMG_LIST]);

    const deleteImg = useCallback(
        ({ photo_id }: USER_PHOTO, idx: number) => {
            if (!isLogin()) {
                const newImgList = [...imgList];
                newImgList.splice(idx, 1);
                setSession(
                    IMG_TRY_ON_NOT_LOGIN_INFO_CACHE,
                    newImgList.filter((it) => it.customerEyePhotoType === 2),
                );
                SET_IMG_LIST(newImgList);
            } else {
                if (delBtnLoading) {
                    return;
                }
                SET_DEL_LOADING(true);
                unPhoto({ photo_id })
                    .then(() => {
                        getPhotoList();
                    })
                    .finally(() => {
                        SET_DEL_LOADING(false);
                    });
            }
        },
        [SET_DEL_LOADING, SET_IMG_LIST, delBtnLoading, getPhotoList, imgList],
    );

    // 把用户选择的图片放在内容中，准备裁剪
    function onSelectImg(ev: any) {
        const file = ev.target.files[0];
        if (!file) {
            return;
        }
        ev.target.value = "";
        SET_USR_UPLOAD_FILE(file);
    }

    // 上传图片按钮的点击事件，这个事件触发input标签的click方法，触发上边的onSelectImg回调函数
    const onClickUpload = useCallback(() => {
        if (loading) {
            return;
        }
        gaCommonEvent("upload", {
            event_category: "try on",
        });
        // 只有获取到用户上传图片列表后，并且列表中的数量小于允许上传的最大数量才允许上传
        const usrUploadList = imgList.filter(
            (i: USER_PHOTO) => i.customerEyePhotoType === 2,
        );
        if (usrUploadList.length < MAX_UPLOAD_CNT) {
            if (!getSession(USED_IMAGE_TRY_ON)) {
                // 如果用户没有体验过，给用户一个提示
                setSession(USED_IMAGE_TRY_ON, "-");
                SET_FIRST_NOTICE(true);
            } else {
                // 直接选择图片
                // @ts-ignore
                refInput.current.click();
            }
        } else {
            SET_NOT_ALLOW_UPLOAD(true);
        }
    }, [SET_FIRST_NOTICE, SET_NOT_ALLOW_UPLOAD, imgList, loading]);

    // 点击重新上传。先选择文件，然后隐藏重新上传modal，然后上传，最后删除之前上传的图片。
    const onReuploadImg = useCallback(
        (ev: any) => {
            gaCommonEvent("upload", {
                event_category: "try on",
            });

            const payload = {
                usrConfirmImg: null,
            };
            SET_USR_CONFIRM_IMG_INFO(payload);

            const file = ev.target.files[0];
            if (!file) {
                return;
            }
            ev.target.value = "";
            SET_USR_UPLOAD_FILE(file);

            if (isLogin()) {
                unPhoto({ photo_id: usrConfirmImg?.photo_id });
            }
        },
        [
            SET_USR_CONFIRM_IMG_INFO,
            SET_USR_UPLOAD_FILE,
            usrConfirmImg?.photo_id,
        ],
    );

    // 提交瞳孔信息给后台
    const onSubmit = useCallback(() => {
        if (!usrConfirmImg) {
            return;
        }
        getImgWidthHeight(usrConfirmImg.photo_img).then(({ width, height }) => {
            const proportion = notEyeImgTooHigh
                ? imgBoxHeight / height
                : imgBoxWidth / width;
            let [lEyeX, lEyeY] = lPosition;
            let [rEyeX, rEyeY] = rPosition;
            // 坐标是svg图片的。图片的中心坐标需要加上宽高的一半，
            const offset = 13;
            lEyeX = lEyeX + offset;
            lEyeY = lEyeY + offset;
            rEyeX = rEyeX + offset;
            rEyeY = rEyeY + offset;
            if (notEyeImgTooHigh) {
                // 如果太高了，横坐标还要减去一部分
                const left =
                    (imgBoxWidth - (width * imgBoxHeight) / height) / 2;
                lEyeX -= left;
                rEyeX -= left;
            } else {
                // 如果太宽了，纵坐标需要减去一部分
                const top = (imgBoxHeight - (height * imgBoxWidth) / width) / 2;
                lEyeY -= top;
                rEyeY -= top;
            }
            // 除以比例，把屏幕上得到的坐标映射到原图上
            lEyeX /= proportion;
            lEyeY /= proportion;
            rEyeX /= proportion;
            rEyeY /= proportion;
            // 如果左右眼坐标反了，这个做一下处理
            if (lEyeX > rEyeX) {
                const [tx, ty] = [lEyeX, lEyeY];
                [lEyeX, lEyeY] = [rEyeX, rEyeY];
                [rEyeX, rEyeY] = [tx, ty];
            }
            if (!isLogin()) {
                let item = {
                    ...usrConfirmImg,
                    lEyeX,
                    lEyeY,
                    rEyeX,
                    rEyeY,
                };
                const newImgList = [...imgList];
                newImgList.unshift(item);
                try {
                    setSession(
                        IMG_TRY_ON_NOT_LOGIN_INFO_CACHE,
                        newImgList.filter(
                            (it) => it.customerEyePhotoType === 2,
                        ),
                    );
                } catch (err) {
                    console.error("tryon set session cache error", err);
                }
                AFTER_SAVE_EYE_POSITION(newImgList);
            } else {
                postPhotoEyePositionJava(
                    usrConfirmImg.photo_path,
                    usrConfirmImg.photo_id,
                    lEyeX,
                    lEyeY,
                    rEyeX,
                    rEyeY,
                ).then(() => {
                    const img = {
                        ...usrConfirmImg,
                        lEyeX,
                        lEyeY,
                        rEyeX,
                        rEyeY,
                        customerEyePhotoType: 2,
                    };
                    const newImgList = [...imgList];
                    newImgList.unshift(img);
                    AFTER_SAVE_EYE_POSITION(newImgList);
                });
            }
        });
    }, [
        AFTER_SAVE_EYE_POSITION,
        imgBoxHeight,
        imgBoxWidth,
        imgList,
        lPosition,
        notEyeImgTooHigh,
        rPosition,
        usrConfirmImg,
    ]);

    // 点击人脸照片调用函数。 组建mount之后自动调用函数，参数是第一张图片
    const onSelectVisibleImg = useCallback(
        (imgInfo: USER_PHOTO) => {
            let { lEyeY, lEyeX, rEyeX, rEyeY, photo_img } = imgInfo;
            // 小于0的是模特图
            const userPd =
                imgInfo.photo_id < 0
                    ? imgInfo.customers_pd
                    : getLocalItem(USR_PD) || imgInfo.customers_pd || defaultPd;
            const payload = {
                tooHigh: true,
                lt: [0, 0],
                usrPd: userPd,
                selectPd: userPd,
                currImg: imgInfo,
            };
            if (lEyeY && lEyeX && rEyeX && rEyeY) {
                const img = new Image();
                img.crossOrigin = "anonymous";
                img.onerror = () => {
                    SHOW_IMG(payload);
                };
                img.onload = () => {
                    // 2、确定宽高
                    const originWidth = img.width;
                    const originHeight = img.height;
                    const tooHigh =
                        originHeight / originWidth > imgBoxHeight / imgBoxWidth;
                    let left = 0;
                    let top = 0;
                    const proportion = tooHigh
                        ? imgBoxWidth / originWidth
                        : imgBoxHeight / originHeight;

                    // @ts-ignore
                    lEyeY *= proportion;
                    // @ts-ignore
                    lEyeX *= proportion;
                    // @ts-ignore
                    rEyeX *= proportion;
                    // @ts-ignore
                    rEyeY *= proportion;
                    if (tooHigh) {
                        // 太高了
                        // @ts-ignore
                        if (lEyeY < imgBoxHeight && rEyeY < imgBoxHeight) {
                            // 眼睛在容器内
                            if (
                                // @ts-ignore
                                lEyeY > (imgBoxHeight * 2) / 3 ||
                                // @ts-ignore
                                rEyeY > (imgBoxHeight * 2) / 3
                            ) {
                                // 如果眼睛在最下边1/3区域。上移一部分。
                                top = -imgBoxHeight / 3;
                            }
                        } else {
                            // 眼睛在容器外
                            top =
                                // @ts-ignore
                                (imgBoxHeight - Math.abs(lEyeY - rEyeY)) / 2 -
                                // @ts-ignore
                                (lEyeY < rEyeY ? lEyeY : rEyeY);
                        }
                        if (top > 0) {
                            top = 0;
                        } else {
                            const finalHeight = originHeight * proportion;
                            if (finalHeight + top < imgBoxHeight) {
                                top = finalHeight - imgBoxHeight;
                            }
                        }
                    } else {
                        // 太宽了。 让眼镜在屏幕最中间
                        left =
                            // @ts-ignore
                            (imgBoxWidth - Math.abs(lEyeX - rEyeX)) / 2 -
                            // @ts-ignore
                            (lEyeX < rEyeX ? lEyeX : rEyeX);
                        if (left > 0) {
                            left = 0;
                        } else {
                            const finalWidth = originWidth * proportion;
                            if (finalWidth + left < imgBoxWidth) {
                                left = finalWidth - imgBoxWidth;
                            }
                        }
                    }
                    const payload = {
                        tooHigh,
                        lt: [left, top],
                        usrPd: userPd,
                        selectPd: userPd,
                        currImg: imgInfo,
                    };
                    SHOW_IMG(payload);
                };
                img.src = photo_img;
            } else {
                SHOW_IMG(payload);
            }
        },
        [SHOW_IMG, imgBoxHeight, imgBoxWidth],
    );

    // 取消裁剪
    function onCropperCancel() {
        SET_USR_UPLOAD_FILE(null);
    }

    const changeImgLoading = useCallback(
        (loading: boolean) => {
            SET_IMG_LOADING(loading);
        },
        [SET_IMG_LOADING],
    );

    // 裁剪完成
    const onCropperSubmit = useCallback(
        async (blob: Blob, dataUrl: string) => {
            const fileType = "jpeg";
            const fileName =
                getS3Folder() + "small-" + getFileHash() + "." + fileType;
            SET_UPLOAD_LOADING(true);
            try {
                const imgInfo: any = await postPhotoJava(
                    fileName,
                    blob,
                    "1",
                    defaultPd,
                    isLogin() ? 2 : 3,
                );

                if (!isLogin()) {
                    // 用户未登录的情况下，缺少一些属性，手动补全
                    imgInfo.photo_id = getFileHash();
                    imgInfo.photo_img = dataUrl;
                }
                if (
                    imgInfo.rEyeX === undefined &&
                    imgInfo.rEyeY === undefined &&
                    imgInfo.lEyeX === undefined &&
                    imgInfo.lEyeY === undefined
                ) {
                    // 没有瞳距。需要手动定位
                    let reader = new FileReader();
                    reader.onload = (ev1: any) => {
                        const img = new Image();
                        img.crossOrigin = "anonymous";
                        img.onload = () => {
                            const payload = {
                                notEyeImgTooHigh:
                                    img.height / img.width >
                                    imgBoxHeight / imgBoxWidth,
                                usrConfirmImg: imgInfo,
                            };
                            SET_USR_CONFIRM_IMG_INFO(payload);
                        };
                        img.src = ev1.target.result;
                    };
                    reader.readAsDataURL(blob);
                } else {
                    // 返回了瞳距信息，直接把数据插入到imgList头部
                    const newImgList = [...imgList];
                    newImgList.unshift(imgInfo);
                    if (!isLogin()) {
                        setSession(
                            IMG_TRY_ON_NOT_LOGIN_INFO_CACHE,
                            newImgList.filter(
                                (it) => it.customerEyePhotoType === 2,
                            ),
                        );
                    }
                    SET_IMG_LIST(newImgList);
                }
            } catch (e) {
                console.error(e);

                SET_UPLOAD_ERROR(true);
            } finally {
                // 滑动图片列表到最新上传的那张图片
                const line = document.querySelector(".img-line");
                if (line) {
                    const intervalId = setInterval(() => {
                        line.scrollLeft += -12;
                        if (line.scrollLeft <= 0) {
                            clearInterval(intervalId);
                        }
                    }, 16);
                }
                SET_UPLOAD_LOADING(false);
                SET_USR_UPLOAD_FILE(null); // 提交成功，也要更新状态为null, 表示当前没有要裁剪的图片; 后面图片和瞳孔相关的提交就和当前裁剪管理相关模块无关
            }
        },
        [
            SET_IMG_LIST,
            SET_UPLOAD_ERROR,
            SET_UPLOAD_LOADING,
            SET_USR_CONFIRM_IMG_INFO,
            SET_USR_UPLOAD_FILE,
            imgBoxHeight,
            imgBoxWidth,
            imgList,
        ],
    );

    const ref1 = useRef(null);
    useDrag(
        ref1,
        { x: lPosition[0], y: lPosition[1] },
        ({ x, y }: { x: number; y: number }) => {
            SET_L_POSITION([x, y]);
        },
    );
    const ref2 = useRef(null);
    useDrag(
        ref2,
        { x: rPosition[0], y: rPosition[1] },
        ({ x, y }: { x: number; y: number }) => {
            SET_R_POSITION([x, y]);
        },
    );
    const refInput = useRef(null);

    const glassesCache = useRef<any>({ loadList: [] });

    /*
      从接口获取图片列表
     */
    useEffect(() => {
        // 从本地缓存获取pd信息
        const userPd = getLocalItem(USR_PD);
        if (userPd) {
            setTimeout(() => {
                SET_USR_PD_H5(userPd);
                SET_SELECT_PD(userPd);
            }, 500);
        }

        fixBody();

        if (!isLogin()) {
            try {
                // 如果没有登录，从本地缓存读取图片列表
                const cacheList =
                    getSession(IMG_TRY_ON_NOT_LOGIN_INFO_CACHE) ?? [];
                // @ts-ignore
                const imgList = [].concat(cacheList, modelImgEyeList);
                SET_IMG_LIST(imgList);
            } catch (err) {
                console.error("tryon get session cache error", err);
            }
        } else {
            getPhotoList();
        }

        return () => {
            cancelFixBody();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 切换眼镜后，需要重新戴眼镜
    useEffect(() => {
        if (!productImg) {
            return;
        }

        const payload = glassesCache.current[productImg];
        if (payload) {
            SET_GLASSES_INFO(payload);
        } else {
            if (!imgLoading) {
                SET_IMG_LOADING(true);
            }
            glassesCache.current.loadList.push(productImg);
            removeBlank(productImg)
                .then(({ dataUrl, width, height, status }) => {
                    setHiddenGlass(status === 404); // 这里加了一个对图片的错误处理，当图片为404时，可以设置为隐藏该图片
                    const payload = {
                        glassesImgSrc: dataUrl,
                        width: width,
                        height: height,
                    };
                    glassesCache.current[productImg] = payload;
                    SET_GLASSES_INFO(payload);
                })
                .finally(() => {
                    glassesCache.current.loadList =
                        glassesCache.current.loadList.filter(
                            (it: string) => it !== productImg,
                        );
                    if (glassesCache.current.loadList.length === 0) {
                        SET_IMG_LOADING(false);
                    }
                });
        }
    }, [SET_GLASSES_INFO, SET_IMG_LOADING, imgLoading, productImg]);

    // 切换颜色眼镜后，需要重新戴眼镜
    useEffect(() => {
        if (!productColorImg) {
            return;
        }

        const payload = glassesCache.current[productColorImg];
        if (payload) {
            SET_GLASSES_COLOR_INFO(payload);
        } else {
            if (!imgLoading) {
                SET_IMG_LOADING(true);
            }
            glassesCache.current.loadList.push(productColorImg);
            removeBlank(productColorImg)
                .then(({ dataUrl, width, height }) => {
                    const payload = {
                        glassesColorImgSrc: dataUrl,
                        width,
                        height,
                    };
                    glassesCache.current[productColorImg] = payload;
                    SET_GLASSES_COLOR_INFO(payload);
                })
                .finally(() => {
                    glassesCache.current.loadList =
                        glassesCache.current.loadList.filter(
                            (it: string) => it !== productColorImg,
                        );
                    if (glassesCache.current.loadList.length === 0) {
                        SET_IMG_LOADING(false);
                    }
                });
        }
    }, [SET_GLASSES_COLOR_INFO, SET_IMG_LOADING, imgLoading, productColorImg]);

    // 显示图片列表的第一张图片
    useEffect(() => {
        if (imgList.length) {
            onSelectVisibleImg(imgList[0]);
        }
    }, [imgList, onSelectVisibleImg]);

    useEffect(() => {
        if (!currImg || !glassesSize) return;
        (async () => {
            const { width, height } = await getImgWidthHeight(
                currImg.photo_img,
            );
            if (
                currImg.rEyeX !== undefined &&
                currImg.rEyeY !== undefined &&
                currImg.lEyeX !== undefined &&
                currImg.lEyeY !== undefined
            ) {
                // 原图坐标转换最终坐标。需要计算一个比例，计算比例前，需要判断图片的宽高比
                const proportion = tooHigh
                    ? imgBoxWidth / width
                    : imgBoxHeight / height;
                let lx = currImg.lEyeX * proportion;
                let ly = currImg.lEyeY * proportion;
                let rx = currImg.rEyeX * proportion;
                let ry = currImg.rEyeY * proportion;
                const style = wearGlasses(
                    lx,
                    ly,
                    rx,
                    ry,
                    width,
                    height,
                    glassesSize[0],
                    glassesSize[1],
                    usrPd,
                    defaultPd,
                    lt,
                );
                SET_STYLE(style);
            } else {
                SET_STYLE(defaultStyle);
            }
        })();
    }, [
        SET_STYLE,
        currImg,
        defaultStyle,
        glassesSize,
        imgBoxHeight,
        imgBoxWidth,
        lt,
        tooHigh,
        usrPd,
    ]);

    useEffect(() => {
        let intervalId: any = null;
        if (!mouseLocation) {
            return;
        }
        const line = document.querySelector(".img-line");
        if (!line) {
            return;
        }
        intervalId = setInterval(() => {
            line.scrollLeft += mouseLocation === "r" ? 3 : -3;
        }, 16);
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [mouseLocation]);

    const [isShowColorAni, setIsShowColorAni] = useState(false);
    const PdContent = useMemo(
        () => (
            <div className={stl["pd-content"]}>
                <div
                    className={stl["pd-desc"]}
                    dangerouslySetInnerHTML={{
                        __html: t("common:ImgTryOn:PdDesc"),
                    }}
                />
                <div className={stl["pd-image-container"]}>
                    <FmImage
                        className={stl["pd-example"]}
                        image="https://df5apg8r0m634.cloudfront.net/images/2021/1028/290eeecfeec04ef3f8b2f31f606a4ac3.jpg"
                        alt="pd example"
                        width={220}
                        height={100}
                    />
                    <p className={stl["pd-title"]}>{t("common:ImgTryOn:PD")}</p>
                    <FmSelect
                        dataList={pdOptions}
                        onChange={onSelectValChange}
                        value={selectPd}
                        className={classnames(stl["pd-select"], "pd-select")}
                        MenuProps={{
                            classes: {
                                paper: classnames(
                                    stl["pd-options-paper"],
                                    "pd-options-paper",
                                ),
                            },
                        }}
                    ></FmSelect>
                    <FmButton
                        className={classnames(
                            "gtm-img-try-on-submit",
                            stl["pd-submit"],
                            stl["btn"],
                        )}
                        onClick={onMobilePdChange}
                    >
                        {t("common:ImgTryOn:Submit")}
                    </FmButton>
                </div>
            </div>
        ),
        [onMobilePdChange, onSelectValChange, pdOptions, selectPd, t],
    );

    const imgLoadingDom = useMemo(() => {
        return (
            <div className={stl["loading"]}>
                <FmLoading visible={imgLoading || delBtnLoading} />
            </div>
        );
    }, [delBtnLoading, imgLoading]);

    const glassesColorImageDom = useMemo(() => {
        return (
            !usrConfirmImg &&
            style && (
                <>
                    {(!productColorImg || isShowColorAni) && (
                        <FmImage
                            image={glassesImgSrc}
                            style={style}
                            className={classnames(
                                stl["glasses"],
                                "glasses-image",
                                { ["hidden"]: hiddenGlass },
                            )}
                            alt=""
                        />
                    )}
                    {productColorImg && glassesColorImgSrc && (
                        <FmImage
                            image={glassesColorImgSrc}
                            style={style}
                            className={`glasses ${
                                isShowColorAni ? "ani-glasses" : ""
                            }`}
                            alt=""
                        />
                    )}
                </>
            )
        );
    }, [
        glassesColorImgSrc,
        glassesImgSrc,
        hiddenGlass,
        isShowColorAni,
        productColorImg,
        style,
        usrConfirmImg,
    ]);

    const userConfirmImgDom = useMemo(() => {
        console.log(usrConfirmImg, currImg);
        return usrConfirmImg ? (
            <div className={stl["manual-box"]}>
                <FmImage
                    image={usrConfirmImg.photo_img}
                    style={{
                        ...(notEyeImgTooHigh
                            ? { height: "100%" }
                            : { width: "100%" }),
                        ...(notEyeImgTooHigh
                            ? {}
                            : {
                                  top: "50%",
                                  transform: "translateY(-50%)",
                              }),
                    }}
                    className={stl["people"]}
                    alt=""
                />
            </div>
        ) : (
            currImg && (
                <>
                    <FmImage
                        image={currImg.photo_img}
                        style={{
                            position: "relative",
                            ...(tooHigh
                                ? { width: "100%" }
                                : { height: "100%" }),
                            left: lt[0] + "px",
                            top: lt[1] + "px",
                        }}
                        className={stl["people"]}
                        alt=""
                    />
                    <div className={stl["try-on-products-title"]}>
                        {tryOnProduct.products_name_attribute ||
                            tryOnProduct.products_model}
                    </div>
                </>
            )
        );
    }, [
        currImg,
        lt,
        notEyeImgTooHigh,
        tooHigh,
        tryOnProduct.products_model,
        tryOnProduct.products_name_attribute,
        usrConfirmImg,
    ]);

    const noticeShowDom = useMemo(() => {
        return (
            noticeShow && (
                <div className={stl["notice-box"]}>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: t("common:ImgTryOn:OnlyForReference"),
                        }}
                    />
                    <FmIcon
                        onClick={() => SET_NOTICE_SHOW(false)}
                        icon="icon--close"
                        className={stl["set-notice-show"]}
                        fontSize="inherit"
                    />
                </div>
            )
        );
    }, [SET_NOTICE_SHOW, noticeShow, t]);

    const userConfirmImgDomToShowOrNot = useMemo(() => {
        return !usrConfirmImg ? (
            <div
                className={classnames(
                    stl["user-confirm-img"],
                    "user-confirm-img",
                )}
            >
                <SelectLine
                    imgList={imgList}
                    onSelectVisibleImg={onSelectVisibleImg}
                    currImg={currImg}
                    deleteImg={deleteImg}
                    loading={loading}
                    changeImgLoading={changeImgLoading}
                    onClickUpload={onClickUpload}
                    setColorAni={setIsShowColorAni}
                />
                <div className={stl["bottom-line-btns"]}>
                    <FmButton
                        onClick={() => SET_PD_MODAL(true)}
                        variant={"outlined"}
                        buttonColor={variable.text}
                    >
                        {t("common:ImgTryOn:EditPD")}
                    </FmButton>
                    <TryOnProductDec />
                </div>
            </div>
        ) : (
            <div className={stl["user-confirm"]}>
                <div
                    className={stl["detected"]}
                    dangerouslySetInnerHTML={{
                        __html: t("common:ImgTryOn:CannotDetectFacialFeatures"),
                    }}
                />
                <div className={stl["btns"]}>
                    <div
                        className={classnames(
                            stl["re-upload"],
                            "btn gtm-img-try-on-re-upload",
                        )}
                    >
                        <span>{t("common:ImgTryOn:ReUpload")}</span>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={onReuploadImg}
                            className={stl["reupload-input"]}
                        />
                    </div>
                    <FmButton
                        className={classnames(
                            stl["submit"],
                            "btn submit gtm-img-try-on-submit",
                        )}
                        onClick={onSubmit}
                    >
                        {t("common:ImgTryOn:Submit")}
                    </FmButton>
                </div>
            </div>
        );
    }, [
        SET_PD_MODAL,
        changeImgLoading,
        currImg,
        deleteImg,
        imgList,
        loading,
        onClickUpload,
        onReuploadImg,
        onSelectVisibleImg,
        onSubmit,
        t,
        usrConfirmImg,
    ]);
    return (
        <Suspense fallback={<TryOnSkeleton />}>
            <div className={stl["img-try-on"]}>
                <input
                    ref={refInput}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onSelectImg}
                />
                <div
                    className={stl["img-box"]}
                    style={{
                        width: imgBoxWidth + "px",
                        height: imgBoxHeight + "px",
                    }}
                >
                    {userConfirmImgDom}
                    {imgLoadingDom}
                    {glassesColorImageDom}
                    <svg
                        ref={ref1}
                        viewBox="0 0 100 100"
                        style={{
                            display: usrConfirmImg ? "initial" : "none",
                            left: lPosition[0] + "px",
                            top: lPosition[1] + "px",
                        }}
                    >
                        <path
                            d="M 48 0 L 52 0 L 52 100 L 48 100 Z"
                            fill={variable.main}
                        />
                        <path
                            d="M 0 48 L 0 52 L 100 52 L 100 48 Z"
                            fill={variable.main}
                        />
                        <circle
                            cx="50"
                            cy="50"
                            r="34"
                            strokeWidth="4"
                            stroke={variable.main}
                            fill="none"
                        />
                        <circle cx="50" cy="50" r="18" fill={variable.main} />
                    </svg>
                    <svg
                        ref={ref2}
                        viewBox="0 0 100 100"
                        style={{
                            display: usrConfirmImg ? "initial" : "none",
                            left: rPosition[0] + "px",
                            top: rPosition[1] + "px",
                        }}
                    >
                        <path
                            d="M 48 0 L 52 0 L 52 100 L 48 100 Z"
                            fill={variable.main}
                        />
                        <path
                            d="M 0 48 L 0 52 L 100 52 L 100 48 Z"
                            fill={variable.main}
                        />
                        <circle
                            cx="50"
                            cy="50"
                            r="34"
                            strokeWidth="4"
                            stroke={variable.main}
                            fill="none"
                        />
                        <circle cx="50" cy="50" r="18" fill={variable.main} />
                    </svg>
                    {noticeShowDom}
                </div>
                {userConfirmImgDomToShowOrNot}
                {/* 已经上传了50张照片，不能上传更多照片提示 */}
                <FmDrawer
                    anchor="bottom"
                    closeablePosition="right"
                    open={notAllowUploadVisible}
                    closeable
                    onClose={() => SET_NOT_ALLOW_UPLOAD(false)}
                    footer={
                        <FmButton
                            fullWidth
                            className={classnames(
                                "btn btn-ok gtm-img-try-on-max-quality",
                            )}
                            onClick={() => SET_NOT_ALLOW_UPLOAD(false)}
                            variant="outlined"
                            size={"large"}
                        >
                            {t("common:OK")}
                        </FmButton>
                    }
                >
                    <div
                        className={stl["upload-notice"]}
                        dangerouslySetInnerHTML={{
                            __html: t("common:ImgTryOn:MaxUploadFiftyImg"),
                        }}
                    />
                </FmDrawer>
                {/* 第一次使用2D试戴，给出的使用提示 */}

                <FmDrawer
                    open={firstNotice}
                    onClose={() => SET_FIRST_NOTICE(false)}
                    anchor={"bottom"}
                    childrenClassName={classnames(
                        stl["try-on-drawer"],
                        "try-on-drawer",
                    )}
                    headerChildren
                    // fullHeight
                >
                    <div className={classnames(stl["modal-content-box"])}>
                        <div className={stl["first-use-img"]}>
                            <FmImage image="https://df5apg8r0m634.cloudfront.net/images/2021/0326/a8c3d6012af2ef2a4714c933331ecc87.jpg" />
                            <FmImage image="https://df5apg8r0m634.cloudfront.net/images/2021/0326/16896df9b7ac17f3386ceb31c0bb4deb.jpg" />
                        </div>
                        <div
                            className={stl["notice-text"]}
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    "common:ImgTryOn:UploadImgWithoutGlasses",
                                ),
                            }}
                        />
                        <div className={stl["button-container"]}>
                            <FmButton
                                className={classnames(
                                    stl["try-on-upload-before"],
                                    stl["button-select-picture"],
                                    "btn blue-btn gtm-img-try-on-upload",
                                )}
                                onClick={() => {
                                    // @ts-ignore
                                    refInput.current.click();
                                    SET_FIRST_NOTICE(false);
                                }}
                            >
                                {t("common:ImgTryOn:SelectPicture")}
                            </FmButton>
                            <FmButton
                                variant="text"
                                className={classnames(
                                    stl["try-on-upload-before"],
                                    stl["button-back"],
                                    "back gem-img-try-on-back",
                                )}
                                onClick={() => SET_FIRST_NOTICE(false)}
                            >
                                &lt; {t("common:ImgTryOn:Back")}
                            </FmButton>
                        </div>
                    </div>
                </FmDrawer>

                {/* 选择pd */}
                <FmDrawer
                    open={pdModalVisible}
                    onClose={() => SET_PD_MODAL(false)}
                    anchor={"bottom"}
                    headerChildren
                    childrenClassName={classnames(
                        stl["pd-modal-children-container"],
                        "pd-modal-children-container",
                    )}
                >
                    {PdContent}
                </FmDrawer>
                {/* 上传失败的提示 */}
                <FmDrawer
                    open={uploadError}
                    onClose={() => SET_UPLOAD_ERROR(false)}
                    anchor={"bottom"}
                    childrenClassName={classnames(
                        stl["network-error-container"],
                    )}
                    headerChildren
                >
                    <div className={classnames(stl["network"], "network")}>
                        {t("common:ImgTryOn:NetworkError")}
                    </div>
                    <FmButton
                        className={stl["network-reupload"]}
                        onClick={() => {
                            // @ts-ignore
                            refInput.current.click();
                            SET_UPLOAD_ERROR(false);
                        }}
                    >
                        {t("common:ImgTryOn:ReUpload")}
                    </FmButton>
                </FmDrawer>

                <Cropper
                    file={userUploadFile}
                    onCancel={onCropperCancel}
                    onSubmit={onCropperSubmit}
                />
            </div>
        </Suspense>
    );
});

export default ImageTryOn;
